import {
  Prisma,
  type PrismaClient,
  type SalesDocument,
  type SalesDocumentLine,
} from '@prisma/client';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { HttpError } from '../../core/errors.js';
import { nextDocumentNumber } from '../../core/sequence.js';
import { postEntry, reverseEntry, type PostingLine } from '../accounting/posting.service.js';
import type { AuthContext } from '../auth/auth.types.js';
import { BASE_CURRENCY, computeTotals, outstanding, type CalcLineInput } from './calc.js';

type Tx = Prisma.TransactionClient;
type Db = PrismaClient | Tx;
const dec = (v: Decimal.Value | { toString(): string }) => new Decimal(v.toString());

export const DOC_KINDS = ['quotation', 'proforma', 'invoice', 'creditnote'] as const;
export type DocKind = (typeof DOC_KINDS)[number];
export const DOC_PERM: Record<DocKind, string> = {
  quotation: 'sales.quotation',
  proforma: 'sales.proforma',
  invoice: 'sales.invoice',
  creditnote: 'sales.creditnote',
};
export const DOC_LABEL: Record<DocKind, string> = {
  quotation: 'Quotation',
  proforma: 'Proforma invoice',
  invoice: 'Invoice',
  creditnote: 'Credit note',
};

export const REVENUE_KEYS = [
  'REV_SOFTWARE',
  'REV_CONSULT',
  'REV_HOSTING',
  'REV_EQUIP',
  'REV_OTHER',
] as const;

const num = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => /^-?\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid number');
const positive = num.refine((v) => Number(v) > 0, 'Must be greater than zero');
const nonNegative = num.refine((v) => Number(v) >= 0, 'Cannot be negative');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const lineInputSchema = z.object({
  kind: z.enum(['item', 'section']).default('item'),
  itemId: z.number().int().positive().nullable().optional(),
  description: z.string().trim().min(1, 'Description is required').max(1000),
  quantity: positive.default('1'),
  unit: z.string().trim().max(20).nullable().optional(),
  unitPrice: nonNegative.default('0'),
  discountType: z.enum(['none', 'percent', 'amount']).default('none'),
  discountValue: nonNegative.default('0'),
  vatCode: z.string().trim().min(2).max(30).default('VAT_STD'),
  isOptional: z.boolean().default(false),
  isSelected: z.boolean().default(true),
  whtApplicable: z.boolean().default(false),
  revenueAccountKey: z.enum(REVENUE_KEYS).optional(),
  /** One-off line: also add it to Products & Services (created as Incomplete). */
  saveToCatalogue: z.boolean().default(false),
});
export type LineInput = z.infer<typeof lineInputSchema>;

export const documentInputSchema = z.object({
  partyId: z.number().int().positive(),
  contactId: z.number().int().positive().nullable().optional(),
  currency: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase())
    .optional(),
  exchangeRate: positive.optional(),
  issueDate: isoDate,
  dueDate: isoDate.nullable().optional(),
  validUntil: isoDate.nullable().optional(),
  reference: z.string().trim().max(100).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  terms: z.string().trim().max(4000).nullable().optional(),
  lines: z.array(lineInputSchema).min(1, 'Add at least one line').max(200),
  sourceDocumentId: z.number().int().positive().nullable().optional(),
  linkedInvoiceId: z.number().int().positive().nullable().optional(),
  customFields: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
});
export type DocumentInput = z.infer<typeof documentInputSchema>;

export const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
export const isoOf = (d: Date | null | undefined) => (d ? d.toISOString().slice(0, 10) : null);
export const addDays = (d: Date, days: number) => new Date(d.getTime() + days * 86_400_000);

export const docInclude = {
  lines: { orderBy: { lineNo: 'asc' } },
} satisfies Prisma.SalesDocumentInclude;
export type DocWithLines = SalesDocument & { lines: SalesDocumentLine[] };

// ───────────────────────── loading and visibility ─────────────────────────

/** Documents a user may see: own-data scope applies to the document owner. */
export function visibleWhere(auth: AuthContext, kind?: DocKind): Prisma.SalesDocumentWhereInput {
  return {
    companyId: auth.companyId,
    ...(kind ? { docType: kind } : {}),
    ...(auth.scope === 'own' ? { ownerId: auth.userId } : {}),
  };
}

export async function loadDocument(db: Db, auth: AuthContext, id: number): Promise<DocWithLines> {
  const doc = await db.salesDocument.findFirst({
    where: { id, ...visibleWhere(auth) },
    include: docInclude,
  });
  if (!doc) throw new HttpError(404, 'NOT_FOUND', 'Document not found');
  return doc;
}

export const kindOf = (doc: { docType: string }): DocKind => doc.docType as DocKind;

// ───────────────────────── creating and saving drafts ─────────────────────────

async function resolveRate(
  db: Db,
  companyId: number,
  currency: string,
  issueDate: Date,
  given?: string,
): Promise<Decimal> {
  if (currency === BASE_CURRENCY) return new Decimal(1);
  if (given) return dec(given);
  const latest = await db.exchangeRate.findFirst({
    where: { companyId, currency, effectiveDate: { lte: issueDate } },
    orderBy: { effectiveDate: 'desc' },
  });
  if (!latest)
    throw new HttpError(
      422,
      'NO_EXCHANGE_RATE',
      `Enter an exchange rate for ${currency} (Settings > Tax & Currencies) or type one on the document`,
    );
  return dec(latest.rate);
}

/** Validated, priced and ready-to-store lines plus document totals. */
export async function priceLines(
  tx: Db,
  args: {
    companyId: number;
    userId: number;
    currency: string;
    exchangeRate: Decimal;
    lines: LineInput[];
  },
) {
  const taxRates = await tx.taxRate.findMany({
    where: { companyId: args.companyId, kind: 'vat', active: true },
  });
  const items = await tx.item.findMany({
    where: {
      companyId: args.companyId,
      id: { in: args.lines.map((l) => l.itemId).filter((i): i is number => !!i) },
    },
  });
  const resolved: (LineInput & {
    vatRate: Decimal;
    revenueAccountKey: string;
    itemId: number | null;
  })[] = [];

  for (const [i, l] of args.lines.entries()) {
    const rate = taxRates.find((t) => t.code === l.vatCode);
    if (l.kind === 'item' && !rate)
      throw new HttpError(
        400,
        'VALIDATION_ERROR',
        `Line ${i + 1}: VAT category "${l.vatCode}" does not exist`,
      );
    let itemId = l.itemId ?? null;
    let revenueAccountKey = l.revenueAccountKey;
    if (itemId) {
      const item = items.find((x) => x.id === itemId);
      if (!item) throw new HttpError(400, 'VALIDATION_ERROR', `Line ${i + 1}: item not found`);
      revenueAccountKey ??= item.revenueAccountKey as (typeof REVENUE_KEYS)[number];
    } else if (l.kind === 'item' && l.saveToCatalogue) {
      const created = await tx.item.create({
        data: {
          companyId: args.companyId,
          name: l.description.slice(0, 200),
          type: 'service',
          unit: l.unit ?? 'each',
          vatCode: l.vatCode,
          revenueAccountKey: revenueAccountKey ?? 'REV_CONSULT',
          needsReview: true,
          createdBy: args.userId,
          prices: { create: [{ currency: args.currency, price: l.unitPrice }] },
        },
      });
      itemId = created.id;
    }
    resolved.push({
      ...l,
      itemId,
      vatRate: rate ? dec(rate.rate) : new Decimal(0),
      revenueAccountKey: revenueAccountKey ?? 'REV_CONSULT',
    });
  }

  const calc = computeTotals(
    resolved.map<CalcLineInput>((l) => ({
      kind: l.kind,
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      discountType: l.discountType,
      discountValue: l.discountValue,
      vatRate: l.vatRate,
      isOptional: l.isOptional,
      isSelected: l.isSelected,
    })),
    { currency: args.currency, exchangeRate: args.exchangeRate },
  );
  const lineData = resolved.map((l, i) => {
    const c = calc.lines[i]!;
    return {
      lineNo: i + 1,
      kind: l.kind,
      itemId: l.itemId,
      description: l.description,
      quantity: l.quantity,
      unit: l.unit ?? null,
      unitPrice: l.unitPrice,
      discountType: l.discountType,
      discountValue: l.discountValue,
      vatCode: l.vatCode,
      vatRate: l.vatRate.toString(),
      isOptional: l.isOptional,
      isSelected: l.isSelected,
      whtApplicable: l.whtApplicable,
      revenueAccountKey: l.revenueAccountKey,
      lineDiscount: c.lineDiscount.toString(),
      lineNet: c.lineNet.toString(),
      lineTax: c.lineTax.toString(),
      lineTotal: c.lineTotal.toString(),
      baseNet: c.baseNet.toString(),
      baseTax: c.baseTax.toString(),
    };
  });
  return { calc, lineData };
}

export async function saveDraft(
  tx: Tx,
  args: { auth: AuthContext; kind: DocKind; id?: number; input: DocumentInput },
): Promise<DocWithLines> {
  const { auth, kind, input } = args;
  const party = await tx.party.findFirst({
    where: { id: input.partyId, companyId: auth.companyId, active: true },
  });
  if (!party) throw new HttpError(400, 'VALIDATION_ERROR', 'Client not found');
  if (kind === 'invoice' || kind === 'creditnote') {
    if (!party.isClient)
      throw new HttpError(
        409,
        'NOT_A_CLIENT',
        'Convert this prospect to a client before invoicing them',
      );
  } else if (!party.isClient && !party.isProspect) {
    throw new HttpError(409, 'NOT_A_CLIENT', 'Choose a client or prospect');
  }
  if (input.contactId) {
    const contact = await tx.partyContact.findFirst({
      where: { id: input.contactId, partyId: party.id },
    });
    if (!contact)
      throw new HttpError(400, 'VALIDATION_ERROR', 'Contact does not belong to this client');
  }

  const issueDate = toDate(input.issueDate);
  const currency = input.currency ?? party.currency;
  const exchangeRate = await resolveRate(
    tx,
    auth.companyId,
    currency,
    issueDate,
    input.exchangeRate,
  );
  const { calc, lineData } = await priceLines(tx, {
    companyId: auth.companyId,
    userId: auth.userId,
    currency,
    exchangeRate,
    lines: input.lines,
  });

  let dueDate = input.dueDate ? toDate(input.dueDate) : null;
  let validUntil = input.validUntil ? toDate(input.validUntil) : null;
  if (kind === 'invoice' && !dueDate) dueDate = addDays(issueDate, party.paymentTermsDays);
  if ((kind === 'quotation' || kind === 'proforma') && !validUntil)
    validUntil = addDays(issueDate, 30);
  if (dueDate && dueDate < issueDate)
    throw new HttpError(400, 'VALIDATION_ERROR', 'Due date cannot be before the issue date');

  let linkedInvoiceId: number | null = input.linkedInvoiceId ?? null;
  if (kind === 'creditnote') {
    if (!linkedInvoiceId)
      throw new HttpError(400, 'VALIDATION_ERROR', 'A credit note must be linked to an invoice');
    const inv = await tx.salesDocument.findFirst({
      where: {
        id: linkedInvoiceId,
        companyId: auth.companyId,
        docType: 'invoice',
        partyId: party.id,
      },
    });
    if (!inv || !['approved', 'sent', 'partially_paid', 'paid'].includes(inv.status))
      throw new HttpError(409, 'INVALID_INVOICE', 'Choose an issued invoice of this client');
    if (inv.currency !== currency)
      throw new HttpError(409, 'CURRENCY_MISMATCH', 'A credit note must use its invoice currency');
  } else linkedInvoiceId = null;

  const base = {
    partyId: party.id,
    contactId: input.contactId ?? null,
    currency,
    exchangeRate: exchangeRate.toString(),
    issueDate,
    dueDate: kind === 'invoice' ? dueDate : null,
    validUntil: kind === 'quotation' || kind === 'proforma' ? validUntil : null,
    reference: input.reference ?? null,
    notes: input.notes ?? null,
    terms: input.terms ?? null,
    subtotal: calc.subtotal.toString(),
    discountTotal: calc.discountTotal.toString(),
    taxTotal: calc.taxTotal.toString(),
    total: calc.total.toString(),
    baseSubtotal: calc.baseSubtotal.toString(),
    baseTax: calc.baseTax.toString(),
    baseTotal: calc.baseTotal.toString(),
    sourceDocumentId: input.sourceDocumentId ?? null,
    linkedInvoiceId,
    customFields: input.customFields,
    updatedBy: auth.userId,
  };

  if (args.id) {
    const existing = await tx.salesDocument.findFirst({
      where: { id: args.id, ...visibleWhere(auth, kind) },
    });
    if (!existing) throw new HttpError(404, 'NOT_FOUND', 'Document not found');
    if (!['draft', 'pending_approval'].includes(existing.status))
      throw new HttpError(
        409,
        'LOCKED',
        'Only draft documents can be edited. Issued invoices change through credit notes.',
      );
    await tx.salesDocumentLine.deleteMany({ where: { documentId: existing.id } });
    return tx.salesDocument.update({
      where: { id: existing.id },
      data: { ...base, status: 'draft', lines: { create: lineData } },
      include: docInclude,
    });
  }
  return tx.salesDocument.create({
    data: {
      companyId: auth.companyId,
      docType: kind,
      ownerId: auth.userId,
      createdBy: auth.userId,
      status: 'draft',
      ...base,
      lines: { create: lineData },
    },
    include: docInclude,
  });
}

export async function logEvent(
  db: Db,
  args: {
    companyId: number;
    entityType:
      'sales_document' | 'receipt' | 'purchase_order' | 'vendor_bill' | 'expense' | 'payout';
    entityId: number;
    type: string;
    userId?: number | null;
    note?: string;
    meta?: Prisma.InputJsonValue;
  },
) {
  await db.documentEvent.create({
    data: {
      companyId: args.companyId,
      entityType: args.entityType,
      entityId: args.entityId,
      type: args.type,
      userId: args.userId ?? null,
      note: args.note?.slice(0, 500),
      meta: args.meta,
    },
  });
}

// ───────────────────────── approval and posting ─────────────────────────

export function assertWithinLimit(
  auth: AuthContext,
  baseTotal: Decimal.Value | { toString(): string },
) {
  if (auth.approvalLimit !== null && dec(baseTotal).gt(auth.approvalLimit)) {
    throw new HttpError(
      403,
      'APPROVAL_LIMIT_EXCEEDED',
      `This document is above your approval limit (UGX ${Number(auth.approvalLimit).toLocaleString('en-US')}). Submit it for approval by someone with a higher limit.`,
    );
  }
}

/** Ledger lines for an invoice (Dr AR / Cr revenue / Cr VAT output) or, reversed, a credit note. */
function saleLines(doc: DocWithLines, reverse: boolean): PostingLine[] {
  const revenue = new Map<string, Decimal>();
  let vat = new Decimal(0);
  for (const l of doc.lines) {
    if (l.kind !== 'item' || (l.isOptional && !l.isSelected)) continue;
    revenue.set(
      l.revenueAccountKey,
      (revenue.get(l.revenueAccountKey) ?? new Decimal(0)).plus(dec(l.baseNet)),
    );
    vat = vat.plus(dec(l.baseTax));
  }
  const lines: PostingLine[] = [];
  let total = new Decimal(0);
  for (const [key, amount] of revenue) {
    if (amount.isZero()) continue;
    total = total.plus(amount);
    lines.push(
      reverse
        ? { systemKey: key, debit: amount.toFixed(4) }
        : { systemKey: key, credit: amount.toFixed(4) },
    );
  }
  if (!vat.isZero()) {
    total = total.plus(vat);
    lines.push(
      reverse
        ? { systemKey: 'VAT_OUT', debit: vat.toFixed(4) }
        : { systemKey: 'VAT_OUT', credit: vat.toFixed(4) },
    );
  }
  lines.unshift(
    reverse
      ? { systemKey: 'AR', credit: total.toFixed(4), memo: `${doc.number}` }
      : { systemKey: 'AR', debit: total.toFixed(4), memo: `${doc.number}` },
  );
  return lines;
}

/**
 * Issues a document: checks the approver's permission and limit (done by the caller), assigns its
 * gapless number, and for invoices and credit notes posts the ledger entry in the same transaction.
 */
export async function approveDocument(
  tx: Tx,
  auth: AuthContext,
  docId: number,
): Promise<DocWithLines> {
  const doc = await loadDocument(tx, auth, docId);
  const kind = kindOf(doc);
  if (!['draft', 'pending_approval'].includes(doc.status))
    throw new HttpError(409, 'ALREADY_APPROVED', 'This document has already been approved');
  if (!doc.lines.some((l) => l.kind === 'item' && !(l.isOptional && !l.isSelected)))
    throw new HttpError(422, 'NO_LINES', 'Add at least one line item before approving');
  if (dec(doc.total).lte(0) && kind !== 'quotation')
    throw new HttpError(422, 'ZERO_TOTAL', 'The total must be greater than zero');

  const number = doc.number ?? (await nextDocumentNumber(tx, auth.companyId, kind, doc.issueDate));
  const party = await tx.party.findUniqueOrThrow({ where: { id: doc.partyId } });
  const patch: Prisma.SalesDocumentUpdateInput = {
    number,
    status: 'approved',
    approvedAt: new Date(),
    approvedBy: auth.userId,
    updatedBy: auth.userId,
  };

  if (kind === 'invoice') {
    const withNumber = { ...doc, number };
    const entry = await postEntry(tx, {
      companyId: auth.companyId,
      date: doc.issueDate,
      description: `Invoice ${number}: ${party.name}`,
      sourceType: 'invoice',
      sourceId: doc.id,
      reference: number,
      lines: saleLines(withNumber, false),
      userId: auth.userId,
    });
    patch.journalEntryId = entry.id;
  } else if (kind === 'creditnote') {
    if (!doc.linkedInvoiceId)
      throw new HttpError(422, 'NO_INVOICE', 'A credit note must be linked to an invoice');
    const invoice = await tx.salesDocument.findUniqueOrThrow({
      where: { id: doc.linkedInvoiceId },
    });
    const owed = outstanding(invoice);
    const applied = Decimal.min(owed, dec(doc.total));
    const excess = dec(doc.total).minus(applied);
    const lines = saleLines({ ...doc, number }, true);
    let advanceId: number | null = null;
    if (excess.gt(0)) {
      // The invoice is already (partly) paid: the excess becomes credit the client can use elsewhere.
      const rate = dec(doc.exchangeRate);
      const excessBase = excess.times(rate).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
      lines.push(
        { systemKey: 'AR', debit: excessBase.toFixed(4), memo: `Excess credit ${number}` },
        { systemKey: 'CUST_ADV', credit: excessBase.toFixed(4), memo: `Credit note ${number}` },
      );
      const adv = await tx.customerAdvance.create({
        data: {
          companyId: auth.companyId,
          partyId: doc.partyId,
          currency: doc.currency,
          exchangeRate: doc.exchangeRate,
          originalAmount: excess.toString(),
          remainingAmount: excess.toString(),
          sourceType: 'creditnote',
          sourceId: doc.id,
        },
      });
      advanceId = adv.id;
    }
    const entry = await postEntry(tx, {
      companyId: auth.companyId,
      date: doc.issueDate,
      description: `Credit note ${number}: ${party.name}`,
      sourceType: 'creditnote',
      sourceId: doc.id,
      reference: number,
      lines,
      userId: auth.userId,
    });
    patch.journalEntryId = entry.id;
    await tx.salesDocument.update({
      where: { id: invoice.id },
      data: { amountCredited: dec(invoice.amountCredited).plus(applied).toString() },
    });
    await refreshInvoiceStatus(tx, invoice.id);
    void advanceId;
  }

  const updated = await tx.salesDocument.update({
    where: { id: doc.id },
    data: patch,
    include: docInclude,
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'sales_document',
    entityId: doc.id,
    type: 'approved',
    userId: auth.userId,
    note: number,
  });
  if (kind === 'invoice') await autoApplyProformaAdvances(tx, auth, updated);
  return updated;
}

/** Status of an invoice follows its payments: paid / partially paid, else back to sent or approved. */
export async function refreshInvoiceStatus(tx: Tx, invoiceId: number): Promise<void> {
  const inv = await tx.salesDocument.findUniqueOrThrow({ where: { id: invoiceId } });
  if (inv.status === 'void' || inv.status === 'draft' || inv.status === 'pending_approval') return;
  const owed = outstanding(inv);
  const settled = dec(inv.amountPaid).plus(inv.amountCredited);
  const status = owed.isZero()
    ? 'paid'
    : settled.gt(0)
      ? 'partially_paid'
      : inv.sentAt
        ? 'sent'
        : 'approved';
  if (status !== inv.status)
    await tx.salesDocument.update({ where: { id: invoiceId }, data: { status } });
}

// ───────────────────────── advances ─────────────────────────

/** When an invoice grew out of a proforma, advances paid against that proforma are applied at once. */
async function autoApplyProformaAdvances(tx: Tx, auth: AuthContext, invoice: DocWithLines) {
  if (!invoice.sourceDocumentId) return;
  const advances = await tx.customerAdvance.findMany({
    where: {
      companyId: auth.companyId,
      proformaId: invoice.sourceDocumentId,
      remainingAmount: { gt: 0 },
    },
    orderBy: { id: 'asc' },
  });
  for (const adv of advances) {
    const owed = outstanding(
      await tx.salesDocument.findUniqueOrThrow({ where: { id: invoice.id } }),
    );
    if (owed.isZero()) break;
    await applyAdvance(tx, auth, {
      advanceId: adv.id,
      invoiceId: invoice.id,
      amount: Decimal.min(owed, dec(adv.remainingAmount)),
    });
  }
}

/** Dr Customer Advances / Cr Accounts Receivable, at each side's own rate, with any difference to FX. */
export async function applyAdvance(
  tx: Tx,
  auth: AuthContext,
  args: { advanceId: number; invoiceId: number; amount: Decimal },
) {
  const advance = await tx.customerAdvance.findFirst({
    where: { id: args.advanceId, companyId: auth.companyId },
  });
  const invoice = await tx.salesDocument.findFirst({
    where: { id: args.invoiceId, companyId: auth.companyId, docType: 'invoice' },
  });
  if (!advance || !invoice) throw new HttpError(404, 'NOT_FOUND', 'Advance or invoice not found');
  if (advance.partyId !== invoice.partyId)
    throw new HttpError(409, 'PARTY_MISMATCH', 'The advance belongs to a different client');
  if (advance.currency !== invoice.currency)
    throw new HttpError(409, 'CURRENCY_MISMATCH', 'Currencies must match');
  if (!['approved', 'sent', 'partially_paid'].includes(invoice.status))
    throw new HttpError(
      409,
      'INVOICE_NOT_OPEN',
      `Invoice ${invoice.number ?? ''} is not open for payment`,
    );
  if (args.amount.lte(0) || args.amount.gt(dec(advance.remainingAmount)))
    throw new HttpError(409, 'ADVANCE_EXCEEDED', 'Amount exceeds the remaining advance');
  if (args.amount.gt(outstanding(invoice)))
    throw new HttpError(
      409,
      'OVERPAYMENT',
      `Amount exceeds what is owed on invoice ${invoice.number}`,
    );

  const baseDp = 0;
  const advBase = args.amount
    .times(dec(advance.exchangeRate))
    .toDecimalPlaces(baseDp, Decimal.ROUND_HALF_UP);
  const arBase = args.amount
    .times(dec(invoice.exchangeRate))
    .toDecimalPlaces(baseDp, Decimal.ROUND_HALF_UP);
  const lines: PostingLine[] = [
    { systemKey: 'CUST_ADV', debit: advBase.toFixed(4), memo: 'Advance applied' },
    { systemKey: 'AR', credit: arBase.toFixed(4), memo: invoice.number ?? undefined },
  ];
  const diff = advBase.minus(arBase);
  if (diff.gt(0)) lines.push({ systemKey: 'FX_GAIN', credit: diff.toFixed(4) });
  if (diff.lt(0)) lines.push({ systemKey: 'FX_LOSS', debit: diff.abs().toFixed(4) });
  const entry = await postEntry(tx, {
    companyId: auth.companyId,
    date: new Date().toISOString().slice(0, 10),
    description: `Advance applied to invoice ${invoice.number}`,
    sourceType: 'advance',
    sourceId: advance.id,
    lines,
    userId: auth.userId,
  });
  await tx.advanceApplication.create({
    data: {
      advanceId: advance.id,
      invoiceId: invoice.id,
      amount: args.amount.toString(),
      journalEntryId: entry.id,
    },
  });
  await tx.customerAdvance.update({
    where: { id: advance.id },
    data: { remainingAmount: dec(advance.remainingAmount).minus(args.amount).toString() },
  });
  await tx.salesDocument.update({
    where: { id: invoice.id },
    data: { amountPaid: dec(invoice.amountPaid).plus(args.amount).toString() },
  });
  await refreshInvoiceStatus(tx, invoice.id);
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'sales_document',
    entityId: invoice.id,
    type: 'advance_applied',
    userId: auth.userId,
    note: `${args.amount.toString()} ${invoice.currency}`,
  });
}

// ───────────────────────── void ─────────────────────────

export async function voidDocument(
  tx: Tx,
  auth: AuthContext,
  docId: number,
  reason: string,
): Promise<DocWithLines> {
  const doc = await loadDocument(tx, auth, docId);
  const kind = kindOf(doc);
  if (doc.status === 'void')
    throw new HttpError(409, 'ALREADY_VOID', 'This document is already void');
  if (kind === 'invoice' && doc.journalEntryId) {
    const applications = await tx.advanceApplication.count({ where: { invoiceId: doc.id } });
    const allocations = await tx.receiptAllocation.count({
      where: { invoiceId: doc.id, receipt: { status: 'posted' } },
    });
    if (dec(doc.amountCredited).gt(0) || applications > 0 || allocations > 0) {
      throw new HttpError(
        409,
        'HAS_PAYMENTS',
        'This invoice has payments or credit notes. Void those first, or issue a credit note instead.',
      );
    }
    await reverseEntry(tx, {
      companyId: auth.companyId,
      entryId: doc.journalEntryId,
      reason: `Void ${doc.number}: ${reason}`,
      date: new Date().toISOString().slice(0, 10),
      userId: auth.userId,
    });
  }
  if (kind === 'creditnote' && doc.journalEntryId) {
    const invoice = doc.linkedInvoiceId
      ? await tx.salesDocument.findUnique({ where: { id: doc.linkedInvoiceId } })
      : null;
    const advance = await tx.customerAdvance.findFirst({
      where: { sourceType: 'creditnote', sourceId: doc.id },
    });
    if (advance && !dec(advance.remainingAmount).equals(dec(advance.originalAmount)))
      throw new HttpError(
        409,
        'ADVANCE_USED',
        'Part of this credit has already been used. Void that first.',
      );
    await reverseEntry(tx, {
      companyId: auth.companyId,
      entryId: doc.journalEntryId,
      reason: `Void ${doc.number}: ${reason}`,
      date: new Date().toISOString().slice(0, 10),
      userId: auth.userId,
    });
    if (advance) await tx.customerAdvance.delete({ where: { id: advance.id } });
    if (invoice) {
      const applied = dec(doc.total).minus(advance ? dec(advance.originalAmount) : 0);
      await tx.salesDocument.update({
        where: { id: invoice.id },
        data: {
          amountCredited: Decimal.max(0, dec(invoice.amountCredited).minus(applied)).toString(),
        },
      });
      await refreshInvoiceStatus(tx, invoice.id);
    }
  }
  const updated = await tx.salesDocument.update({
    where: { id: doc.id },
    data: {
      status: 'void',
      voidedAt: new Date(),
      voidReason: reason.slice(0, 300),
      updatedBy: auth.userId,
    },
    include: docInclude,
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'sales_document',
    entityId: doc.id,
    type: 'void',
    userId: auth.userId,
    note: reason,
  });
  return updated;
}

// ───────────────────────── conversion and duplication ─────────────────────────

export async function convertDocument(
  tx: Tx,
  auth: AuthContext,
  args: { sourceId: number; to: 'proforma' | 'invoice'; percent: Decimal },
): Promise<DocWithLines> {
  const src = await loadDocument(tx, auth, args.sourceId);
  const kind = kindOf(src);
  const allowed =
    kind === 'quotation' ? ['proforma', 'invoice'] : kind === 'proforma' ? ['invoice'] : [];
  if (!allowed.includes(args.to))
    throw new HttpError(
      409,
      'CANNOT_CONVERT',
      `A ${DOC_LABEL[kind].toLowerCase()} cannot be converted to a ${args.to}`,
    );
  if (!['approved', 'sent', 'viewed', 'accepted'].includes(src.status))
    throw new HttpError(
      409,
      'CANNOT_CONVERT',
      `Only issued ${DOC_LABEL[kind].toLowerCase()}s can be converted (this one is ${src.status.replace('_', ' ')})`,
    );
  const remaining = new Decimal(100).minus(dec(src.billedPercent));
  if (args.percent.lte(0) || args.percent.gt(100))
    throw new HttpError(400, 'VALIDATION_ERROR', 'Percent must be between 0 and 100');
  if (args.percent.gt(remaining))
    throw new HttpError(
      409,
      'ALREADY_BILLED',
      `Only ${remaining.toString()}% of this document is left to convert`,
    );

  const scale = args.percent.div(100);
  const lines: LineInput[] = src.lines
    .filter((l) => l.kind === 'item' && !(l.isOptional && !l.isSelected))
    .map((l) => ({
      kind: 'item' as const,
      itemId: l.itemId,
      description: l.description,
      quantity: dec(l.quantity).times(scale).toDecimalPlaces(4).toString(),
      unit: l.unit,
      unitPrice: dec(l.unitPrice).toString(),
      discountType: l.discountType as 'none' | 'percent' | 'amount',
      discountValue:
        l.discountType === 'amount'
          ? dec(l.discountValue).times(scale).toDecimalPlaces(4).toString()
          : dec(l.discountValue).toString(),
      vatCode: l.vatCode,
      isOptional: false,
      isSelected: true,
      whtApplicable: l.whtApplicable,
      revenueAccountKey: l.revenueAccountKey as (typeof REVENUE_KEYS)[number],
      saveToCatalogue: false,
    }));
  const partial = args.percent.lt(100) || dec(src.billedPercent).gt(0);
  const created = await saveDraft(tx, {
    auth,
    kind: args.to,
    input: {
      partyId: src.partyId,
      contactId: src.contactId,
      currency: src.currency,
      exchangeRate: dec(src.exchangeRate).toString(),
      issueDate: new Date().toISOString().slice(0, 10),
      reference: src.reference,
      notes: [
        src.notes,
        partial
          ? `${args.percent.toString()}% of ${DOC_LABEL[kind].toLowerCase()} ${src.number}`
          : `Based on ${DOC_LABEL[kind].toLowerCase()} ${src.number}`,
      ]
        .filter(Boolean)
        .join('\n\n'),
      terms: src.terms,
      lines,
      sourceDocumentId: src.id,
    },
  });
  const billed = dec(src.billedPercent).plus(args.percent);
  await tx.salesDocument.update({
    where: { id: src.id },
    data: { billedPercent: billed.toString(), ...(billed.gte(100) ? { status: 'converted' } : {}) },
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'sales_document',
    entityId: src.id,
    type: 'converted',
    userId: auth.userId,
    note: `${args.percent.toString()}% to ${args.to}`,
    meta: { targetId: created.id },
  });
  return created;
}

export async function duplicateDocument(
  tx: Tx,
  auth: AuthContext,
  docId: number,
): Promise<DocWithLines> {
  const src = await loadDocument(tx, auth, docId);
  return saveDraft(tx, {
    auth,
    kind: kindOf(src),
    input: {
      partyId: src.partyId,
      contactId: src.contactId,
      currency: src.currency,
      exchangeRate: dec(src.exchangeRate).toString(),
      issueDate: new Date().toISOString().slice(0, 10),
      reference: src.reference,
      notes: src.notes,
      terms: src.terms,
      linkedInvoiceId: src.linkedInvoiceId,
      lines: src.lines.map((l) => ({
        kind: l.kind as 'item' | 'section',
        itemId: l.itemId,
        description: l.description,
        quantity: dec(l.quantity).toString(),
        unit: l.unit,
        unitPrice: dec(l.unitPrice).toString(),
        discountType: l.discountType as 'none' | 'percent' | 'amount',
        discountValue: dec(l.discountValue).toString(),
        vatCode: l.vatCode,
        isOptional: l.isOptional,
        isSelected: l.isSelected,
        whtApplicable: l.whtApplicable,
        revenueAccountKey: l.revenueAccountKey as (typeof REVENUE_KEYS)[number],
        saveToCatalogue: false,
      })),
    },
  });
}

/** Recalculate totals after the client picks which optional lines they want (public accept). */
export async function applyOptionalSelection(
  tx: Tx,
  doc: DocWithLines,
  selection: Record<number, boolean>,
): Promise<void> {
  for (const l of doc.lines) {
    if (l.isOptional && selection[l.id] !== undefined)
      await tx.salesDocumentLine.update({
        where: { id: l.id },
        data: { isSelected: selection[l.id]! },
      });
  }
  const fresh = await tx.salesDocument.findUniqueOrThrow({
    where: { id: doc.id },
    include: docInclude,
  });
  const calc = computeTotals(
    fresh.lines.map<CalcLineInput>((l) => ({
      kind: l.kind as 'item' | 'section',
      quantity: l.quantity.toString(),
      unitPrice: l.unitPrice.toString(),
      discountType: l.discountType as 'none' | 'percent' | 'amount',
      discountValue: l.discountValue.toString(),
      vatRate: l.vatRate.toString(),
      isOptional: l.isOptional,
      isSelected: l.isSelected,
    })),
    { currency: fresh.currency, exchangeRate: fresh.exchangeRate.toString() },
  );
  for (const [i, l] of fresh.lines.entries()) {
    const c = calc.lines[i]!;
    await tx.salesDocumentLine.update({
      where: { id: l.id },
      data: {
        lineDiscount: c.lineDiscount.toString(),
        lineNet: c.lineNet.toString(),
        lineTax: c.lineTax.toString(),
        lineTotal: c.lineTotal.toString(),
        baseNet: c.baseNet.toString(),
        baseTax: c.baseTax.toString(),
      },
    });
  }
  await tx.salesDocument.update({
    where: { id: doc.id },
    data: {
      subtotal: calc.subtotal.toString(),
      discountTotal: calc.discountTotal.toString(),
      taxTotal: calc.taxTotal.toString(),
      total: calc.total.toString(),
      baseSubtotal: calc.baseSubtotal.toString(),
      baseTax: calc.baseTax.toString(),
      baseTotal: calc.baseTotal.toString(),
    },
  });
}

// ───────────────────────── view ─────────────────────────

export const isOverdue = (
  d: {
    docType: string;
    status: string;
    dueDate: Date | null;
    total: Prisma.Decimal;
    amountPaid: Prisma.Decimal;
    amountCredited: Prisma.Decimal;
  },
  today = new Date(),
) =>
  d.docType === 'invoice' &&
  ['approved', 'sent', 'partially_paid'].includes(d.status) &&
  !!d.dueDate &&
  d.dueDate < new Date(today.toISOString().slice(0, 10) + 'T00:00:00Z') &&
  outstanding(d).gt(0);

export function documentView(doc: DocWithLines, extra: { partyName?: string } = {}) {
  const owed = outstanding(doc);
  const overdue = isOverdue(doc);
  return {
    id: doc.id,
    docType: doc.docType,
    number: doc.number,
    status: doc.status,
    /** What to show: an issued, unpaid invoice past its due date reads "overdue". */
    displayStatus: overdue ? 'overdue' : doc.status,
    overdue,
    partyId: doc.partyId,
    partyName: extra.partyName,
    contactId: doc.contactId,
    ownerId: doc.ownerId,
    currency: doc.currency,
    exchangeRate: doc.exchangeRate.toString(),
    issueDate: isoOf(doc.issueDate),
    dueDate: isoOf(doc.dueDate),
    validUntil: isoOf(doc.validUntil),
    reference: doc.reference,
    notes: doc.notes,
    terms: doc.terms,
    subtotal: doc.subtotal.toFixed(4),
    discountTotal: doc.discountTotal.toFixed(4),
    taxTotal: doc.taxTotal.toFixed(4),
    total: doc.total.toFixed(4),
    baseTotal: doc.baseTotal.toFixed(4),
    amountPaid: doc.amountPaid.toFixed(4),
    amountCredited: doc.amountCredited.toFixed(4),
    outstanding: owed.toFixed(4),
    billedPercent: doc.billedPercent.toFixed(4),
    sourceDocumentId: doc.sourceDocumentId,
    linkedInvoiceId: doc.linkedInvoiceId,
    sentAt: doc.sentAt,
    viewedAt: doc.viewedAt,
    acceptedAt: doc.acceptedAt,
    acceptedByName: doc.acceptedByName,
    declinedAt: doc.declinedAt,
    declineReason: doc.declineReason,
    respondedAt: doc.respondedAt,
    approvedAt: doc.approvedAt,
    voidReason: doc.voidReason,
    remindersStopped: doc.remindersStopped,
    needsReview: doc.needsReview,
    createdAt: doc.createdAt,
    lines: doc.lines.map((l) => ({
      id: l.id,
      lineNo: l.lineNo,
      kind: l.kind,
      itemId: l.itemId,
      description: l.description,
      quantity: l.quantity.toString(),
      unit: l.unit,
      unitPrice: l.unitPrice.toString(),
      discountType: l.discountType,
      discountValue: l.discountValue.toString(),
      vatCode: l.vatCode,
      vatRate: l.vatRate.toString(),
      isOptional: l.isOptional,
      isSelected: l.isSelected,
      whtApplicable: l.whtApplicable,
      revenueAccountKey: l.revenueAccountKey,
      lineDiscount: l.lineDiscount.toFixed(4),
      lineNet: l.lineNet.toFixed(4),
      lineTax: l.lineTax.toFixed(4),
      lineTotal: l.lineTotal.toFixed(4),
    })),
  };
}
